import { SEARCH_RESULT_FLASH_OFFER_LEAD_IMAGE_OVERRIDES } from 'constants/recommendations'
import type { PropsWithChildren } from 'react'
import { createContext, useContext, useMemo } from 'react'

interface Props {
  enabled: boolean
}

const SearchResultImageOverrideContext = createContext(false)
const mappedDestinationIds = new Set(
  Object.keys(SEARCH_RESULT_FLASH_OFFER_LEAD_IMAGE_OVERRIDES).map(key => key.split('|')[0]),
)

export function hasSearchResultImageOverrides(destinationId?: string): boolean {
  return !!destinationId && mappedDestinationIds.has(destinationId)
}

export function canActivateSearchResultImageOverride(destinationId: string | undefined, isSpoofed: boolean): boolean {
  return !isSpoofed && hasSearchResultImageOverrides(destinationId)
}

export function prioritiseSearchResultImage<TImage extends { id?: string }>(
  images: Array<TImage>,
  offerId: string,
  destinationId?: string,
  enabled = false,
): Array<TImage> {
  if (!enabled || !destinationId) return images

  const leadImageId = SEARCH_RESULT_FLASH_OFFER_LEAD_IMAGE_OVERRIDES[`${destinationId}|${offerId}`]
  if (!leadImageId || images[0]?.id === leadImageId) return images

  const leadImageIndex = images.findIndex(image => image.id === leadImageId)
  if (leadImageIndex < 0) return images

  return [images[leadImageIndex], ...images.slice(0, leadImageIndex), ...images.slice(leadImageIndex + 1)]
}

export function useSearchResultImages<TImage extends { id?: string }>(
  images: Array<TImage>,
  offerId: string,
  destinationId?: string,
): Array<TImage> {
  const enabled = useContext(SearchResultImageOverrideContext)

  return useMemo(
    () => prioritiseSearchResultImage(images, offerId, destinationId, enabled),
    [destinationId, enabled, images, offerId],
  )
}

function SearchResultImageOverrideProvider({ children, enabled }: PropsWithChildren<Props>) {
  return <SearchResultImageOverrideContext.Provider value={enabled}>
    {children}
  </SearchResultImageOverrideContext.Provider>
}

export default SearchResultImageOverrideProvider
